import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useApp, scrollToSection } from "../store";

const NAV = [
  ["nav_about", "#cabinet"],
  ["nav_services", "#expertises"],
  ["nav_method", "#methode"],
  ["nav_clients", "#clients"],
  ["nav_values", "#valeurs"],
];

export const Header = () => {
  const { c, t, lang, setLang } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (sel) => {
    setOpen(false);
    scrollToSection(sel);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        style={{ top: "var(--announce-h, 0px)" }}
        className={`fixed inset-x-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-[#0A1428]/90 backdrop-blur-xl border-b border-[var(--accent-soft)]/10 shadow-sm"
            : "bg-[#0A1428]/75 backdrop-blur-md border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
          <button
            data-testid="brand-home-button"
            onClick={() => go("#hero")}
            className="flex items-center gap-3 text-left"
          >
            {c("logo_image") ? (
              <img
                src={c("logo_image")}
                alt="Polynésie Gestion et Projet"
                data-testid="brand-logo-image"
                className="h-11 w-auto max-w-[220px] object-contain"
              />
            ) : (
              <>
                <span className="w-10 h-10 shrink-0 grid place-items-center bg-[var(--accent)] text-[#0B132B] font-display font-bold text-xl">
                  G
                </span>
                <span className="leading-tight hidden sm:block">
                  <span className="block font-display font-semibold tracking-wide text-sm text-white">
                    POLYNÉSIE GESTION
                  </span>
                  <span className="block font-mono2 text-[10px] uppercase tracking-[0.35em] text-[var(--accent-soft)]">
                    et Projet
                  </span>
                </span>
              </>
            )}
          </button>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Navigation principale">
            {NAV.map(([key, sel]) => (
              <button
                key={key}
                data-testid={`nav-${key.replace("nav_", "")}-link`}
                onClick={() => go(sel)}
                className="text-sm text-white/60 hover:text-white transition-colors duration-300"
              >
                {t(key)}
              </button>
            ))}
            <Link
              data-testid="nav-news-link"
              to="/actualites"
              className="text-sm text-white/60 hover:text-white transition-colors duration-300"
            >
              {t("nav_news")}
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            <div
              data-testid="lang-toggle"
              className="flex items-center border border-[var(--accent-soft)]/20 rounded-full overflow-hidden"
            >
              {["fr", "en"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-switch-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-3 py-1.5 font-mono2 text-xs uppercase tracking-widest transition-colors duration-300 ${
                    lang === l ? "bg-[var(--accent)] text-white" : "text-white/50 hover:text-white"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <button
              data-testid="header-cta-button"
              onClick={() => go("#contact")}
              className="hidden md:inline-flex items-center gap-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors duration-300"
            >
              {t("cta_start")}
              <ArrowUpRight size={16} />
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="lg:hidden text-white p-2"
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#0A1428]/97 backdrop-blur-xl lg:hidden pt-24 px-8"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-6">
              {[...NAV, ["nav_contact", "#contact"]].map(([key, sel], i) => (
                <motion.button
                  key={key}
                  data-testid={`mobile-nav-${key.replace("nav_", "")}-link`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  onClick={() => go(sel)}
                  className="text-left font-display text-3xl text-white/85 hover:text-[var(--accent-soft)] transition-colors duration-300"
                >
                  {t(key)}
                </motion.button>
              ))}
              <Link
                data-testid="mobile-admin-link"
                to="/admin"
                className="mt-8 font-mono2 text-xs uppercase tracking-[0.3em] text-white/40 hover:text-[var(--accent-soft)] transition-colors duration-300"
              >
                {t("footer_admin")} →
              </Link>
              <Link
                data-testid="mobile-news-link"
                to="/actualites"
                onClick={() => setOpen(false)}
                className="font-mono2 text-xs uppercase tracking-[0.3em] text-white/40 hover:text-[var(--accent-soft)] transition-colors duration-300"
              >
                {t("nav_news")} →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
